import { Stack, Text } from '@mantine/core'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { GpuSelector } from '@/components/devices/GpuSelector/GpuSelector'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { Device, Gpu, GpuMemory } from '@/types/device'
import { isJetsonDevice } from '@/utils/device/getDeviceAttr'
import { getOfferedGpus, getReadableGpus } from '../../helpers/gpuSelection'
import { IntegratedGpuNotice } from './IntegratedGpuNotice'

const CARD_HEIGHT = 150
const MB_PER_GB = 1024

const toGb = (mb: number) => (mb / MB_PER_GB).toFixed(1)

const getMemoryMessage = (gpu: Gpu, memory?: GpuMemory, heldMb = 0) => {
  const totalMb = memory?.totalMb ?? gpu.memory_total_mb

  if (!totalMb) {
    return { id: 'deployments.gpuSettings.memoryUnknown' }
  }

  if (memory?.freeMb === undefined) {
    return {
      id: 'deployments.gpuSettings.totalMemory',
      values: { total: toGb(totalMb) }
    }
  }

  return {
    id: 'deployments.gpuSettings.freeMemory',
    values: {
      free: toGb(Math.min(memory.freeMb + heldMb, totalMb)),
      total: toGb(totalMb)
    }
  }
}

type GpuDetailsProps = {
  gpu: Gpu
  memory?: GpuMemory
  heldMb?: number
}

const GpuDetails = ({ gpu, memory, heldMb = 0 }: GpuDetailsProps) => (
  <Stack gap={0} align="center">
    <Text size="xs" ta="center">
      <FormattedMessage {...getMemoryMessage(gpu, memory, heldMb)} />
    </Text>

    {heldMb > 0 && memory?.freeMb !== undefined && (
      <Text size="xs" c="dimmed" ta="center">
        <FormattedMessage
          id="deployments.gpuSettings.heldMemory"
          values={{ held: toGb(heldMb) }}
        />
      </Text>
    )}

    {gpu.compute_capability && (
      <Text size="xs" c="dimmed" ta="center">
        <FormattedMessage
          id="deployments.gpuSettings.computeCapability"
          values={{ value: gpu.compute_capability }}
        />
      </Text>
    )}
  </Stack>
)

type DeviceGpuPickerProps = {
  device: Device
  value: number[]
  savedVlmGpus?: number[]
  memory?: Record<number, GpuMemory>
  held?: Record<number, number>
  error?: ReactNode
  onChange: (value: number[]) => void
}

export const DeviceGpuPicker = ({
  device,
  value,
  savedVlmGpus,
  memory,
  held,
  error,
  onChange
}: DeviceGpuPickerProps) => {
  if (isJetsonDevice(device)) {
    return <IntegratedGpuNotice />
  }

  const gpus = getReadableGpus(device)

  if (gpus.length === 0) {
    return (
      <PlatformAlert variant="warning" compact>
        <FormattedMessage id="deployments.gpuSettings.noGpuInfo" />
      </PlatformAlert>
    )
  }

  const offeredGpus = getOfferedGpus(device, savedVlmGpus)
  const reservedCount = gpus.length - offeredGpus.length

  if (offeredGpus.length === 0) {
    return (
      <PlatformAlert variant="error" compact>
        <FormattedMessage id="deployments.gpuSettings.allReserved" />
      </PlatformAlert>
    )
  }

  return (
    <Stack gap={4}>
      <GpuSelector
        gpus={offeredGpus}
        value={value}
        error={error}
        cols={{ base: 1, sm: 2 }}
        cardHeight={CARD_HEIGHT}
        renderDetails={(gpu) => (
          <GpuDetails
            gpu={gpu}
            memory={memory?.[gpu.index]}
            heldMb={held?.[gpu.index]}
          />
        )}
        single
        onChange={onChange}
      />

      {reservedCount > 0 && (
        <Text size="xs" c="dimmed">
          <FormattedMessage
            id="deployments.gpuSettings.reservedHidden"
            values={{ count: reservedCount }}
          />
        </Text>
      )}

      <Text size="xs" c="dimmed">
        <FormattedMessage id="deployments.gpuSettings.oneGpu" />
      </Text>
    </Stack>
  )
}
